import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import AccountStatementsAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/account-statements/account-statements.entity';
import { OpentItemsService } from 'app/modules/components/reports/historic/open-item/open-items.service';
import { OpenItemReportExport } from 'app/modules/components/reports/historic/open-item/exports/open-item-report.export';
import {
  CustomerAccountSummary,
  buildOpenItemsGridConfig,
  buildOpenItemsColumnDefs,
  buildOpenItemsSummary,
} from 'app/modules/components/reports/historic/open-item/open-items-grid.config';
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { DecimalFormatPipe } from 'app/pipes/decimal-company.pipe';
import { CompanyCurrencyPipe } from 'app/pipes/currency.pipe';
import { DecimalConfigService } from 'app/services/decimal-config.service';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { ExportDialogComponent } from 'app/layout/export-overlay/dialog/export-dialog.component';
import { ExportOptions } from 'app/layout/export-overlay/tasks/core/export-task.interfaces';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';

export interface OpenItemsModalData {
  businessPartnerMongoId: string;
  customerName: string;
  customerCode: string;
}

@Component({
  selector: 'app-open-items-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    SmartGridComponent,
    DecimalFormatPipe,
    CompanyCurrencyPipe,
  ],
  providers: [OpenItemReportExport],
  templateUrl: './open-items-modal.component.html',
})
export class OpenItemsModalComponent implements OnInit {
  data: AccountStatementsAttributes[] = [];
  loading = false;
  totalItems = 0;
  pageIndex = 1;
  pageSize = 50;
  sortActive: keyof AccountStatementsAttributes = 'creationDate';
  sortDirection: 'ASC' | 'DESC' = 'DESC';
  summary: CustomerAccountSummary | null = null;

  gridConfig = buildOpenItemsGridConfig();
  columnDefs = buildOpenItemsColumnDefs(this.formatCurrencyForFilter.bind(this));

  get currencySymbol(): string {
    return this.companyCurrencyPipe.transform('symbol');
  }

  get isPdfDownloadDisabled(): boolean {
    return this.totalItems === 0;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: OpenItemsModalData,
    private dialogRef: MatDialogRef<OpenItemsModalComponent>,
    private dialog: MatDialog,
    private opentItemsService: OpentItemsService,
    private openItemReportExport: OpenItemReportExport,
    private handlerMessage: HandlerMessageService,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private decimalConfigService: DecimalConfigService,
    private readonly i18n: LanguageService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    const options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: '',
      sort: this.sortActive,
      sortDirection: this.sortDirection,
    };
    this.opentItemsService
      .findByBusinessPartner(this.dialogData.businessPartnerMongoId, options, {})
      .subscribe({
        next: (res) => {
          this.data = res?.data?.data ?? [];
          this.totalItems = res?.data?.pageInfo?.[0]?.total ?? 0;
          this.summary = buildOpenItemsSummary(this.data, this.totalItems, {
            name: this.dialogData.customerName,
            code: this.dialogData.customerCode,
          });
          // Solo la primera página vacía indica que el socio no tiene partidas
          if (this.totalItems === 0) {
            this.handlerMessage.info({
              message: this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.NO_OPEN_ITEMS'),
            });
          }
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.handlerMessage.error({
            message: err.error?.message,
            alternativeMessage: this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.FETCH_ERROR'),
            statusCode: err.status,
          });
        },
      });
  }

  changeSort(e): void {
    this.sortActive = e.active;
    this.sortDirection = e.direction?.toUpperCase() || 'ASC';
    this.load();
  }

  changePage(e): void {
    this.pageIndex = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.load();
  }

  downloadPdf(): void {
    if (this.totalItems === 0) {
      this.handlerMessage.warning({ message: this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.NO_DATA_TOOLTIP') });
      return;
    }
    const options = { search: '', sort: this.sortActive, sortDirection: this.sortDirection };
    this.opentItemsService
      .downloadAccountStatementsPdf(this.dialogData.businessPartnerMongoId, options, {})
      .subscribe({
        next: () => this.handlerMessage.success({ message: this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.DOWNLOAD_OK') }),
        error: (error) => {
          this.handlerMessage.error({
            message: error.error?.message,
            alternativeMessage: this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.DOWNLOAD_ERROR'),
            statusCode: error.status,
          });
        },
      });
  }

  openExport(): void {
    const id = 'open-items';
    if (!this.openItemReportExport.canStartTask(id)) return;
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.EXPORT_TITLE') },
    });
    dialogRef.afterClosed().subscribe((exportOptions: ExportOptions) => {
      if (!exportOptions) return;
      const options = { search: '', sort: this.sortActive, sortDirection: this.sortDirection };
      this.openItemReportExport.export(
        exportOptions.format,
        exportOptions.range,
        this.totalItems,
        this.pageSize,
        this.pageIndex,
        id,
        this.i18n.t('DOCUMENT_GENERATE.OPEN_ITEMS_MODAL.EXPORT_TITLE'),
        undefined,
        this.dialogData.businessPartnerMongoId,
        options,
        {},
      );
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  // Formato de moneda para los filtros de columna; usa los decimales configurados
  private formatCurrencyForFilter(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    const decimalPipe = new DecimalPipe('en-US');
    const decimals = this.decimalConfigService.getDecimals();
    return decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? '-';
  }
}
